(() => {
  if (customElements.get('af-wrong-shell')) return;

  class AFWrongShell extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.menu = this.querySelector('[data-af-wrong-menu]');
      this.triggers = [...this.querySelectorAll('[data-af-wrong-menu-open]')];
      this.closeButton = this.querySelector('[data-af-wrong-menu-close]');
      this.search = this.querySelector('details-modal');
      this.megaGroup = this.querySelector('[data-af-wrong-shop]');
      this.mega = this.querySelector('[data-af-wrong-mega]');
      this.megaToggle = this.querySelector('[data-af-wrong-mega-toggle]');
      this.desktopViewport = window.matchMedia('(min-width: 1180px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.menu) return;

      if (this.megaGroup && this.mega) {
        this.mega.hidden = false;
        this.mega.inert = true;
        this.megaExplicitOpen = false;
        this.megaGroup.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'touch' && this.desktopViewport.matches) this.openMega();
        }, { signal });
        this.megaGroup.addEventListener('pointerleave', () => {
          this.megaCloseTimer = setTimeout(() => {
            if (!this.mega.contains(document.activeElement)) this.closeMega();
          }, 160);
        }, { signal });
        this.megaGroup.addEventListener('focusin', (event) => {
          if (event.target !== this.megaToggle) this.openMega();
        }, { signal });
        this.megaGroup.addEventListener('focusout', () => {
          this.megaCloseTimer = setTimeout(() => {
            if (!this.megaGroup.contains(document.activeElement) && !this.megaGroup.matches(':hover')) this.closeMega();
          }, 0);
        }, { signal });
        this.megaToggle?.addEventListener('click', () => {
          if (this.megaGroup.classList.contains('is-open') && this.megaExplicitOpen) this.closeMega();
          else this.openMega(true);
        }, { signal });
        this.megaGroup.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.closeMega();
            this.megaToggle?.focus({ preventScroll: true });
            this.closeMega();
          }
        }, { signal });
        document.addEventListener('pointerdown', (event) => {
          if (!this.megaGroup.contains(event.target)) this.closeMega();
        }, { signal });
      }

      this.desktopViewport.addEventListener('change', () => {
        this.closeMega();
        if (this.desktopViewport.matches && this.menu.open) this.closeMenu(false, true);
      }, { signal });

      this.querySelectorAll('[data-af-wrong-letter-hover]').forEach((link) => {
        if (link.querySelector('.af-wrong-letter')) return;
        const label = link.textContent.trim();
        link.setAttribute('aria-label', label);
        link.replaceChildren(...Array.from(label).map((letter, index) => {
          const span = document.createElement('span');
          span.className = 'af-wrong-letter';
          span.setAttribute('aria-hidden', 'true');
          span.style.setProperty('--af-letter-index', index);
          span.textContent = letter === ' ' ? '\u00a0' : letter;
          return span;
        }));
      });

      this.triggers.forEach((trigger) => trigger.addEventListener('click', () => this.openMenu(trigger), { signal }));
      this.closeButton?.addEventListener('click', () => this.closeMenu(), { signal });
      this.menu.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeMenu();
      }, { signal });
      this.menu.addEventListener('keydown', (event) => this.trapMenuFocus(event), { signal });
      this.menu.addEventListener('click', (event) => {
        if (event.target === this.menu) this.closeMenu();
        if (event.target.closest('a[href]')) this.closeMenu(false, true);
      }, { signal });
      this.menu.addEventListener('close', () => {
        this.menu.classList.remove('is-open');
        document.body.classList.remove('af-wrong-menu-open');
        this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      }, { signal });

      this.menu.querySelectorAll('details').forEach((group) => {
        const summary = group.querySelector('summary');
        const update = () => summary?.setAttribute('aria-expanded', String(group.open));
        group.addEventListener('toggle', update, { signal });
        update();
      });

      const searchDetails = this.search?.querySelector('details');
      if (searchDetails) {
        searchDetails.addEventListener('toggle', () => {
          if (searchDetails.open) {
            this.closeMega();
            if (this.menu.open) this.closeMenu(false, true);
          }
        }, { signal });
      }

      this.resizeObserver = new ResizeObserver(() => {
        const height = this.querySelector('[data-af-header]')?.getBoundingClientRect().height;
        if (height) document.documentElement.style.setProperty('--af-wrong-measured-header-height', `${height}px`);
        document.documentElement.style.setProperty('--af-wrong-header-context-height', `${this.getBoundingClientRect().height}px`);
      });
      const header = this.querySelector('[data-af-header]');
      if (header) this.resizeObserver.observe(header);
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      clearTimeout(this.megaCloseTimer);
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
      if (this.menu?.open) this.menu.close();
      document.body.classList.remove('af-wrong-menu-open');
    }

    openMenu(trigger) {
      clearTimeout(this.closeTimer);
      this.closeMega();
      this.returnFocus = trigger;
      if (this.search?.querySelector('details[open]')) this.search.close(false);
      const cartDrawer = document.querySelector('cart-drawer.active');
      if (cartDrawer?.close) cartDrawer.close();
      if (!this.menu.open) this.menu.showModal();
      document.body.classList.add('af-wrong-menu-open');
      this.triggers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      requestAnimationFrame(() => {
        this.menu.classList.add('is-open');
        this.closeButton?.focus({ preventScroll: true });
      });
    }

    openMega(explicit = false) {
      if (!this.mega || !this.desktopViewport.matches) return;
      clearTimeout(this.megaCloseTimer);
      if (explicit) this.megaExplicitOpen = true;
      this.mega.inert = false;
      this.megaGroup.classList.add('is-open');
      this.megaToggle?.setAttribute('aria-expanded', 'true');
    }

    closeMega() {
      if (!this.mega) return;
      clearTimeout(this.megaCloseTimer);
      this.megaExplicitOpen = false;
      this.megaGroup.classList.remove('is-open');
      this.mega.inert = true;
      this.megaToggle?.setAttribute('aria-expanded', 'false');
    }

    closeMenu(restoreFocus = true, immediate = false) {
      if (!this.menu.open) return;
      clearTimeout(this.closeTimer);
      this.menu.classList.remove('is-open');
      const finish = () => {
        if (this.menu.open) this.menu.close();
        document.body.classList.remove('af-wrong-menu-open');
        this.triggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      };
      if (immediate || this.reducedMotion.matches) finish();
      else this.closeTimer = setTimeout(finish, 300);
    }

    trapMenuFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = [...this.menu.querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled]), [tabindex="0"]')]
        .filter((element) => element.getClientRects().length && !element.closest('[hidden], [inert]'));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('af-wrong-shell', AFWrongShell);
})();
